import { useQuery } from '@tanstack/react-query';

import type {
  BillingPeriod,
  BillingStanding,
  ChargingModel,
  SubscriptionPaymentKind,
  SubscriptionPaymentMethod,
  DocumentType,
  FeatureCode,
  FinancialYearStatus,
  PlanLimitKind,
  SubscriptionAccessState,
  TenantStatus,
} from '@/api/generated/enums';

import { companyFetch, platformFetch } from './client';

/*
 * The calls the platform panel makes, typed. Platform-plane reads and writes go through
 * `platformFetch`; anything that writes a company's own data — branches, years, numbering, the
 * starter catalogue — goes through `companyFetch` and a setup session, because those are the
 * company's endpoints with the company's rules, not a second implementation of them.
 */

export interface Company {
  tenantId: number;
  tenantCode: string;
  tenantName: string;
  status: TenantStatus;
  isActive: boolean;
  supportAccessConsent: boolean;
  planCode: string | null;
  accessState: SubscriptionAccessState;
  activeUsers: number;
  activeBranches: number;
  financialYears: number;
  lastLoginOn: string | null;
  wentLiveOn: string | null;
  createdOn: string;
  /** The server's word for where the company stands with what it owes — never re-derived here. */
  standing: BillingStanding;
  chargingModel: ChargingModel | null;
  billingPeriod: BillingPeriod | null;
  agreedRecurringAmount: number | null;
  /** The last day paid for; null when open-ended. */
  paidUntil: string | null;
}

/** The due list's three tiles, in rupees. Overdue counts read-only companies on charged terms too. */
export interface BillingDues {
  dueSoonCount: number;
  dueSoonAmount: number;
  overdueCount: number;
  overdueAmount: number;
  receivedThisMonth: number;
  noTermsCount: number;
}

export interface Plan {
  code: string;
  name: string;
  description: string | null;
  rank: number;
  trialDays: number;
  isPublic: boolean;
  features: FeatureCode[];
  limits: { kind: PlanLimitKind; value: number }[];
  /** The active list prices, one per way of charging and period. Empty until somebody prices it. */
  prices: PlanPrice[];
}

/** One list price, in rupees. `oneTimeAmount` is the licence fee, null for a subscription. */
export interface PlanPrice {
  chargingModel: ChargingModel;
  billingPeriod: BillingPeriod;
  recurringAmount: number;
  oneTimeAmount: number | null;
}

/*
 * What one company pays the platform (docs/Platform_Billing_Design.md). Dates are `YYYY-MM-DD`
 * calendar days in India; amounts are rupees. `standing` is the server's word for where the
 * company stands — the panel words it and never re-derives it.
 */

export interface ReceiptIssuer {
  name: string;
  address: string | null;
  phone: string | null;
  email: string | null;
  gstin: string | null;
}

export interface BillingTerms {
  chargingModel: ChargingModel;
  billingPeriod: BillingPeriod | null;
  agreedRecurringAmount: number | null;
  agreedOneTimeAmount: number | null;
  listRecurringAmount: number | null;
  listOneTimeAmount: number | null;
  isNegotiated: boolean;
  priceNote: string | null;
  pilotEndsOn: string | null;
}

export interface SuggestedPayment {
  kind: SubscriptionPaymentKind;
  amount: number;
  coversFrom: string;
  coversTo: string;
}

export interface SubscriptionPayment {
  id: number;
  receiptNumber: string;
  receivedOn: string;
  kind: SubscriptionPaymentKind;
  amount: number;
  method: SubscriptionPaymentMethod | null;
  reference: string | null;
  coversFrom: string | null;
  coversTo: string | null;
  note: string | null;
  recordedOn: string;
  recordedBy: string;
  voidedOn: string | null;
  voidedBy: string | null;
  voidReason: string | null;
}

export interface TenantBilling {
  tenantId: number;
  tenantCode: string;
  tenantName: string;
  tenantGstin: string | null;
  issuer: ReceiptIssuer;
  planCode: string | null;
  tenantStatus: TenantStatus;
  terms: BillingTerms | null;
  standing: BillingStanding;
  accessState: SubscriptionAccessState;
  accessEffectiveUntil: string | null;
  paidUntil: string | null;
  graceDays: number;
  cancelledOn: string | null;
  cancellationReason: string | null;
  suggested: SuggestedPayment | null;
  payments: SubscriptionPayment[];
}

export interface BillingTermsInput {
  planCode: string;
  chargingModel: ChargingModel;
  billingPeriod: BillingPeriod | null;
  agreedRecurringAmount: number | null;
  agreedOneTimeAmount: number | null;
  priceNote: string | null;
  pilotEndsOn: string | null;
  graceDays: number | null;
}

export interface PaymentInput {
  kind: SubscriptionPaymentKind;
  receivedOn: string;
  amount: number;
  method: SubscriptionPaymentMethod | null;
  reference: string | null;
  coversFrom: string | null;
  coversTo: string | null;
  note: string | null;
}

export interface FeatureOverride {
  feature: FeatureCode;
  isGranted: boolean;
  reason: string;
  expiresOn: string | null;
  isInEffect: boolean;
}

export interface OnboardingStep {
  step: string;
  isComplete: boolean;
  detail: string | null;
}

export interface Onboarding {
  tenantId: number;
  tenantCode: string;
  tenantName: string;
  isLive: boolean;
  wentLiveOn: string | null;
  steps: OnboardingStep[];
}

export interface ProvisionInput {
  code: string;
  name: string;
  legalName?: string | undefined;
  gstin?: string | undefined;
  pan?: string | undefined;
  contactEmail?: string | undefined;
  contactPhone?: string | undefined;
  ownerUsername: string;
  ownerMobile: string;
  ownerFullName: string;
  ownerEmail?: string | undefined;
  planCode: string;
  startTrial: boolean;
}

export interface Provisioned {
  tenantId: number;
  tenantCode: string;
  tenantName: string;
  owner: { username: string };
  temporaryPassword: string;
  planCode: string;
}

export interface Branch {
  id: number;
  code: string;
  name: string;
  isHeadOffice: boolean;
  gstin: string | null;
  stateCode: string | null;
  stateName: string | null;
  city: string | null;
  isActive: boolean;
}

export interface BranchInput {
  code: string;
  name: string;
  isHeadOffice: boolean;
  gstin?: string | undefined;
  stateCode?: string | undefined;
  stateName?: string | undefined;
  addressLine1?: string | undefined;
  city?: string | undefined;
  pincode?: string | undefined;
  contactPhone?: string | undefined;
}

export interface FinancialYear {
  id: number;
  code: string;
  startDate: string;
  endDate: string;
  status: FinancialYearStatus;
  isCurrent: boolean;
}

export interface NumberingSeries {
  branchId: number;
  financialYearId: number;
  documentType: DocumentType;
  prefix: string;
}

export interface StarterCategory {
  code: string;
  name: string;
  isAccessory: boolean;
  turnaroundBufferDays: number;
  attributes: string[];
  children: StarterCategory[];
}

export interface StarterCatalogue {
  attributes: { code: string; name: string; values: string[] }[];
  categories: StarterCategory[];
}

export interface StarterResult {
  categoriesAdded: string[];
  categoriesPresent: string[];
  attributesAdded: string[];
  attributesPresent: string[];
  valuesAdded: number;
  assignmentsAdded: number;
}

export const platformKeys = {
  companies: ['platform', 'companies'] as const,
  plans: ['platform', 'plans'] as const,
  onboarding: (id: number) => ['platform', 'onboarding', id] as const,
  overrides: (id: number) => ['platform', 'overrides', id] as const,
  billing: (id: number) => ['platform', 'billing', id] as const,
  // Under the companies key on purpose: every billing write already invalidates `companies`, and a
  // prefix match carries the tiles with it — so no write can refresh the list and leave them stale.
  dues: ['platform', 'companies', 'dues'] as const,
  branches: (id: number) => ['platform', 'company', id, 'branches'] as const,
  years: (id: number) => ['platform', 'company', id, 'years'] as const,
  numbering: (id: number) => ['platform', 'company', id, 'numbering'] as const,
  starter: (id: number) => ['platform', 'company', id, 'starter'] as const,
};

// ---- platform plane -------------------------------------------------------------------------

export function useCompanies() {
  return useQuery({
    queryKey: platformKeys.companies,
    queryFn: () => platformFetch<Company[]>('/api/platform/tenants/health'),
  });
}

export function usePlans() {
  return useQuery({
    queryKey: platformKeys.plans,
    queryFn: () => platformFetch<Plan[]>('/api/platform/plans'),
    staleTime: 5 * 60_000,
  });
}

export function useOnboarding(tenantId: number) {
  return useQuery({
    queryKey: platformKeys.onboarding(tenantId),
    queryFn: () => platformFetch<Onboarding>(`/api/platform/tenants/${tenantId}/onboarding`),
  });
}

export function useFeatureOverrides(tenantId: number) {
  return useQuery({
    queryKey: platformKeys.overrides(tenantId),
    queryFn: () =>
      platformFetch<FeatureOverride[]>(`/api/platform/tenants/${tenantId}/feature-overrides`),
  });
}

export function useDues() {
  return useQuery({
    queryKey: platformKeys.dues,
    queryFn: () => platformFetch<BillingDues>('/api/platform/billing/dues'),
  });
}

export function useTenantBilling(tenantId: number) {
  return useQuery({
    queryKey: platformKeys.billing(tenantId),
    queryFn: () => platformFetch<TenantBilling>(`/api/platform/tenants/${tenantId}/billing`),
  });
}

const billingPath = (tenantId: number, rest: string) => `/api/platform/tenants/${tenantId}/${rest}`;

/** New terms: ends the current subscription row and starts another, paid days carried over. */
export const setBillingTerms = (tenantId: number, input: BillingTermsInput) =>
  platformFetch<TenantBilling>(billingPath(tenantId, 'terms'), { method: 'PUT', body: input });

/** Returns the receipt. */
export const recordPayment = (tenantId: number, input: PaymentInput) =>
  platformFetch<SubscriptionPayment>(billingPath(tenantId, 'payments'), {
    method: 'POST',
    body: input,
  });

export const voidPayment = (tenantId: number, paymentId: number, reason: string) =>
  platformFetch<SubscriptionPayment>(billingPath(tenantId, `payments/${paymentId}/void`), {
    method: 'POST',
    body: { reason },
  });

export const setGraceDays = (tenantId: number, graceDays: number) =>
  platformFetch<TenantBilling>(billingPath(tenantId, 'grace'), {
    method: 'PUT',
    body: { graceDays },
  });

export const suspendCompany = (tenantId: number) =>
  platformFetch<TenantBilling>(billingPath(tenantId, 'suspend'), { method: 'POST' });

export const reactivateCompany = (tenantId: number) =>
  platformFetch<TenantBilling>(billingPath(tenantId, 'reactivate'), { method: 'POST' });

/** The customer's notice. Access runs to the paid-through date, then reads only. */
export const cancelCompany = (tenantId: number, reason: string) =>
  platformFetch<TenantBilling>(billingPath(tenantId, 'cancel'), {
    method: 'POST',
    body: { reason },
  });

/** Replaces a plan's list prices wholesale — a price left out is withdrawn. */
export const setPlanPrices = (planCode: string, prices: PlanPrice[]) =>
  platformFetch<PlanPrice[]>(`/api/platform/plans/${encodeURIComponent(planCode)}/prices`, {
    method: 'PUT',
    body: { prices },
  });

export const provisionCompany = (input: ProvisionInput) =>
  platformFetch<Provisioned>('/api/platform/tenants', { method: 'POST', body: input });

export const setFeatureOverride = (
  tenantId: number,
  feature: FeatureCode,
  isGranted: boolean,
  reason: string,
) =>
  platformFetch<FeatureOverride>(`/api/platform/tenants/${tenantId}/feature-overrides`, {
    method: 'PUT',
    body: { feature, isGranted, reason },
  });

export const clearFeatureOverride = (tenantId: number, feature: FeatureCode) =>
  platformFetch<void>(`/api/platform/tenants/${tenantId}/feature-overrides/${feature}`, {
    method: 'DELETE',
  });

export const goLive = (tenantId: number) =>
  platformFetch<Onboarding>(`/api/platform/tenants/${tenantId}/go-live`, { method: 'POST' });

export const changePlatformPassword = (currentPassword: string, newPassword: string) =>
  platformFetch<void>('/api/platform/auth/change-password', {
    method: 'POST',
    body: { currentPassword, newPassword },
  });

// ---- the company's own endpoints, through a setup session -----------------------------------

export function useCompanyBranches(tenantId: number, enabled: boolean) {
  return useQuery({
    queryKey: platformKeys.branches(tenantId),
    queryFn: () => companyFetch<Branch[]>(tenantId, '/api/branch'),
    enabled,
  });
}

export function useCompanyYears(tenantId: number, enabled: boolean) {
  return useQuery({
    queryKey: platformKeys.years(tenantId),
    queryFn: () => companyFetch<FinancialYear[]>(tenantId, '/api/financial-year'),
    enabled,
  });
}

export function useCompanyNumbering(tenantId: number, enabled: boolean) {
  return useQuery({
    queryKey: platformKeys.numbering(tenantId),
    queryFn: () => companyFetch<NumberingSeries[]>(tenantId, '/api/document-numbering-series'),
    enabled,
  });
}

export function useStarterCatalogue(tenantId: number, enabled: boolean) {
  return useQuery({
    queryKey: platformKeys.starter(tenantId),
    queryFn: () => companyFetch<StarterCatalogue>(tenantId, '/api/item-category/starter-template'),
    enabled,
    staleTime: Infinity,
  });
}

export const createBranch = (tenantId: number, input: BranchInput) =>
  companyFetch<Branch>(tenantId, '/api/branch', { method: 'POST', body: input });

export const createFinancialYear = (
  tenantId: number,
  input: { code: string; startDate: string; endDate: string; isCurrent: boolean },
) => companyFetch<FinancialYear>(tenantId, '/api/financial-year', { method: 'POST', body: input });

export const createStandardSeries = (tenantId: number, branchId: number, financialYearId: number) =>
  companyFetch<{ added: NumberingSeries[]; alreadyPresent: DocumentType[] }>(
    tenantId,
    '/api/document-numbering-series/standard',
    { method: 'POST', body: { branchId, financialYearId }, branchId },
  );

export const applyStarterCatalogue = (tenantId: number) =>
  companyFetch<StarterResult>(tenantId, '/api/item-category/starter-template', { method: 'POST' });
